import React, { useState, useEffect, useRef, useMemo } from 'react';
import AdminProductsRepository from '../../services/AdminProductsRepository.js';
import AdminRepository from '../../services/AdminRepository.js';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import { AdminCardGridShimmer } from '../common/Shimmer.jsx';
import { IconAlertTriangle, IconChevronDown, IconChevronUp, IconInfo } from '../common/AdminIcons.jsx';
import {
  MAX_LICENSE_KEYS_PER_PRODUCT,
  normalizeLicenseKey,
  licenseKeyInputsFromProduct,
  parseLicenseKeysFromInputs,
  buildSelectableRegistryKeys,
  buildSelectableKeysForProductSlot,
} from '../../utils/licenseKeyUtils.js';
import {
  VISIBILITY_SCOPES,
  VISIBILITY_SCOPE_OPTIONS,
  normalizeVisibilityScope,
  isActiveForStorage,
} from '../../utils/productVisibility.js';
import ProductLicenseKeyEditor from './ProductLicenseKeyEditor.jsx';
import { useDestructiveAction } from '../../contexts/DestructiveActionContext.jsx';
import {
  RegisteredLicenseKeySelectRow,
  EmptyLicenseSlotSelectRow,
} from './LicenseKeyEditor.jsx';
import {
  formatTrialDaysFieldValue,
  validateTrialDaysField,
} from '../../utils/productTrial.js';
import { useScreenProbe } from '../../utils/screenTiming.js';
import { can } from '../../config/adminPermissions.js';
import './AdminProductManagement.css';
import './LicenseKeyEditor.css';

const EXISTING_SUBSCRIBER_POLICIES = {
  GRANDFATHER: 'grandfather',
  MIGRATE_AT_RENEWAL: 'migrate_at_renewal',
  MIGRATE_ALL: 'migrate_all',
};

const EXISTING_SUBSCRIBER_POLICY_OPTIONS = [
  {
    value: EXISTING_SUBSCRIBER_POLICIES.MIGRATE_AT_RENEWAL,
    label: '次回更新時に新価格へ切替',
    description: '既存契約者は今月分は旧価格のまま。通常は月の変わり目（次回請求）から新価格が適用されます。日割り差額は発生しません。',
  },
  {
    value: EXISTING_SUBSCRIBER_POLICIES.MIGRATE_ALL,
    label: '即時・全員一律で新価格へ移行',
    description: '既存の Stripe サブスクを直ちに新価格へ更新します。日割り差額が発生する場合があります。',
  },
  {
    value: EXISTING_SUBSCRIBER_POLICIES.GRANDFATHER,
    label: '旧価格のまま据え置き',
    description: '既存の契約者・試用中ユーザーは旧価格のまま。新規契約のみ新価格が適用されます。',
  },
];

const ExistingSubscriberPolicyField = ({ value, onChange, namePrefix = 'existing-subscriber-policy' }) => (
  <fieldset className="product-visibility-fieldset product-subscriber-policy-fieldset">
    <legend>既存契約者の扱い（料金変更時）</legend>
    {EXISTING_SUBSCRIBER_POLICY_OPTIONS.map((option) => (
      <label key={option.value} className="product-visibility-option">
        <input
          type="radio"
          name={namePrefix}
          value={option.value}
          checked={value === option.value}
          onChange={() => onChange(option.value)}
        />
        <span className="product-visibility-option-text">
          <strong>{option.label}</strong>
          <span>{option.description}</span>
        </span>
      </label>
    ))}
  </fieldset>
);

function toSafeHttpUrl(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return parsed.href;
  } catch {
    return '';
  }
  return '';
}

function ProductLaunchUrlLine({ label, href }) {
  const raw = String(href || '').trim();
  if (!raw) {
    return <span className="url-unset">{label}: 未設定</span>;
  }
  const safe = toSafeHttpUrl(raw);
  if (!safe) {
    return <span className="url-set" title={raw}>{label}: {raw}</span>;
  }
  return (
    <span className="url-set">
      {label}:{' '}
      <a
        className="product-launch-link"
        href={safe}
        target="_blank"
        rel="noopener noreferrer"
        title={safe}
        onClick={(e) => e.stopPropagation()}
      >
        {safe}
      </a>
    </span>
  );
}

/**
 * プロダクト（モジュール）管理コンポーネント
 */
const AdminProductManagement = ({ user }) => {
  const probe = useScreenProbe('products');
  const { requestDestructiveAction } = useDestructiveAction();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [isSaving, setIsSaving] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [stripeProcessing, setStripeProcessing] = useState(null); // 処理中のproductId
  const [stripeMessage, setStripeMessage] = useState(null); // { type: 'success' | 'error', text: string }
  
  // 確認モーダル用状態
  const [showStripeCreateModal, setShowStripeCreateModal] = useState(false);
  const [showStripeUpdateModal, setShowStripeUpdateModal] = useState(false);
  const [showUnsyncedPriceModal, setShowUnsyncedPriceModal] = useState(false);
  const [unsyncedPriceModalContext, setUnsyncedPriceModalContext] = useState(null);
  const [savedPriceSnapshot, setSavedPriceSnapshot] = useState(null);
  const [existingSubscriberPolicy, setExistingSubscriberPolicy] = useState(
    EXISTING_SUBSCRIBER_POLICIES.MIGRATE_AT_RENEWAL
  );
  const [stripeTargetProduct, setStripeTargetProduct] = useState(null);
  const [actionError, setActionError] = useState('');
  const [registeredLicenseKeys, setRegisteredLicenseKeys] = useState([]);
  const [registryKeysLoading, setRegistryKeysLoading] = useState(false);

  const productsRepo = AdminProductsRepository.getInstance();
  const adminRepo = AdminRepository.getInstance();
  const canWrite = can(user, 'products:write');
  const canDelete = can(user, 'products:delete');

  useEffect(() => {
    if (viewMode !== 'edit') return;
    let cancelled = false;
    setRegistryKeysLoading(true);
    adminRepo.getRegisteredLicenseKeys()
      .then((keys) => {
        if (!cancelled) setRegisteredLicenseKeys(keys);
      })
      .catch(() => {
        if (!cancelled) setRegisteredLicenseKeys([]);
      })
      .finally(() => {
        if (!cancelled) setRegistryKeysLoading(false);
      });
    return () => { cancelled = true; };
  }, [viewMode]);

  const createLicenseKeys = parseLicenseKeysFromInputs(editingProduct?.licenseKeyInputs || []);
  const createSelectableKeys = useMemo(
    () => buildSelectableRegistryKeys(registeredLicenseKeys, createLicenseKeys),
    [registeredLicenseKeys, createLicenseKeys]
  );

  // アイコンオプション
  const iconOptions = [
    { value: 'growth', label: '成長・発達' },
    { value: 'mft', label: 'MFT・筋機能' },
    { value: 'insurance', label: '保険・点数' },
    { value: 'mouthpiece', label: 'マウスピース' },
    { value: 'inventory', label: '在庫管理' },
    { value: 'analytics', label: '分析' },
    { value: 'chart', label: 'カルテ' },
    { value: 'portal', label: 'ポータル' },
    { value: 'default', label: 'デフォルト' }
  ];

  useEffect(() => {
    loadProducts();
  }, []);

  // メニュー外クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openMenuId && !e.target.closest('.product-actions-menu')) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openMenuId]);

  const loadProducts = async ({ syncEditingProductId, silent = false } = {}) => {
    if (!silent) {
      probe.start('load');
      setIsLoading(true);
      setError(null);
    }
    try {
      const data = await productsRepo.getAllProducts();
      setProducts(data);
      if (syncEditingProductId) {
        const updated = data.find((p) => p.id === syncEditingProductId);
        if (updated) {
          setEditingProduct((prev) => {
            if (!prev || prev.id !== syncEditingProductId) return prev;
            return {
              ...prev,
              ...updated,
              visibilityScope: normalizeVisibilityScope(updated),
            };
          });
        }
      }
      if (!silent) {
        probe.end('load', 'ok');
        probe.unlock();
      }
    } catch (err) {
      console.error('製品読み込みエラー:', err);
      if (!silent) {
        probe.end('load', `error: ${err?.message || 'unknown'}`);
        setError('製品データの読み込みに失敗しました');
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  const capturePriceSnapshot = (product) => ({
    monthlyPrice: Number(product?.monthlyPrice) || 0,
    isFree: product?.isFree === true,
  });

  const needsStripeSyncAfterPriceChange = (product, snapshot) => {
    if (!product?.id || !snapshot) return false;
    if (product.isFree || Number(product.monthlyPrice) === 0) return false;
    if (!product.stripeProductId || !product.stripePriceId) return false;
    return Number(product.monthlyPrice) !== Number(snapshot.monthlyPrice);
  };

  const handleCreateNew = () => {
    if (!canWrite) return;
    setSavedPriceSnapshot(null);
    setEditingProduct({
      name: '',
      licenseKeyInputs: ['', '', ''],
      shortDescription: '',
      description: '',
      features: [],
      useCases: [],
      monthlyPrice: 0,
      isComingSoon: false,
      isFree: false,
      freeNote: '',
      priceNote: '',
      iconType: 'default',
      visibilityScope: VISIBILITY_SCOPES.PUBLIC,
      isActive: true,
      launchUrl: '',
      launchUrlDev: '',
      trialPeriodDays: '',
      betaTrialPeriodDays: '',
    });
    setViewMode('edit');
  };

  const openEditView = (product) => {
    if (!canWrite) return;
    setSavedPriceSnapshot(capturePriceSnapshot(product));
    setEditingProduct({
      ...product,
      visibilityScope: normalizeVisibilityScope(product),
      trialPeriodDays: formatTrialDaysFieldValue(product.trialPeriodDays),
      betaTrialPeriodDays: formatTrialDaysFieldValue(product.betaTrialPeriodDays),
    });
    setViewMode('edit');
    setOpenMenuId(null);
  };

  const handleBackToList = () => {
    setViewMode('list');
    setEditingProduct(null);
    setSavedPriceSnapshot(null);
    setShowUnsyncedPriceModal(false);
    setUnsyncedPriceModalContext(null);
  };

  const handleCardClick = (product, e) => {
    if (!canWrite) return;
    if (e.target.closest('button, a, .product-actions-menu, input, select, textarea')) {
      return;
    }
    openEditView(product);
  };

  const handleCardKeyDown = (product, e) => {
    if (!canWrite) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openEditView(product);
    }
  };

  const handleOpenDeleteModal = async (product) => {
    const result = await requestDestructiveAction({
      title: '製品削除',
      message: `製品「${product.name}」を削除します。この操作は取り消せません。`,
      confirmLabel: '削除する',
    });
    if (!result?.reason) return;

    try {
      await productsRepo.deleteProduct(product.id, result.reason);
      await loadProducts();
    } catch (err) {
      console.error('削除エラー:', err);
      setActionError('削除に失敗しました');
    }
  };

  // Stripe作成モーダルを開く
  const handleOpenStripeCreateModal = (product) => {
    if (!canWrite) return;
    if (product.isFree || product.monthlyPrice === 0) {
      setStripeMessage({ type: 'error', text: '無料製品はStripe連携不要です' });
      return;
    }

    if (product.stripePriceId) {
      setStripeMessage({ type: 'error', text: '既にStripe製品が作成されています' });
      return;
    }

    setStripeTargetProduct(product);
    setActionError('');
    setShowStripeCreateModal(true);
  };

  // Stripe作成モーダルを閉じる
  const handleCloseStripeCreateModal = () => {
    setShowStripeCreateModal(false);
    setStripeTargetProduct(null);
    setActionError('');
  };

  // Stripe製品作成を実行
  const handleConfirmStripeCreate = async () => {
    if (!canWrite || !stripeTargetProduct) return;

    setStripeProcessing(stripeTargetProduct.id);
    setStripeMessage(null);

    try {
      const result = await productsRepo.createStripeProduct(stripeTargetProduct.id);
      setStripeMessage({ type: 'success', text: `Stripe製品を作成しました（${result.stripePriceId}）` });
      handleCloseStripeCreateModal();
      await loadProducts({ syncEditingProductId: stripeTargetProduct.id, silent: true });
    } catch (err) {
      console.error('Stripe製品作成エラー:', err);
      setActionError(err.message || 'Stripe製品の作成に失敗しました');
    } finally {
      setStripeProcessing(null);
    }
  };

  const resetExistingSubscriberPolicy = () => {
    setExistingSubscriberPolicy(EXISTING_SUBSCRIBER_POLICIES.MIGRATE_AT_RENEWAL);
  };

  const formatStripeSyncSuccessMessage = (result, prefix = '') => {
    const lead = prefix ? `${prefix} ` : '';
    if (!result.priceChanged) {
      return `${lead}Stripe製品を更新しました`.trim();
    }
    if (result.existingSubscriberPolicy === EXISTING_SUBSCRIBER_POLICIES.MIGRATE_ALL) {
      const migrated = result.migrationStats?.stripeMigrated ?? 0;
      const contractsUpdated = result.migrationStats?.contractsUpdated ?? 0;
      return `${lead}Stripe価格を同期し、既存契約 ${migrated} 件・契約レコード ${contractsUpdated} 件を即時・新価格へ移行しました（${result.stripePriceId}）`.trim();
    }
    if (result.existingSubscriberPolicy === EXISTING_SUBSCRIBER_POLICIES.MIGRATE_AT_RENEWAL) {
      const migrated = result.migrationStats?.stripeMigrated ?? 0;
      const contractsUpdated = result.migrationStats?.contractsUpdated ?? 0;
      return `${lead}Stripe価格を同期し、既存契約 ${migrated} 件・契約レコード ${contractsUpdated} 件を次回更新時から新価格へ切替予定にしました（${result.stripePriceId}）`.trim();
    }
    return `${lead}Stripe価格を同期しました（既存契約者は旧価格のまま / ${result.stripePriceId}）`.trim();
  };

  const buildStripeSyncMessage = (result, prefix = '') => {
    const text = formatStripeSyncSuccessMessage(result, prefix);
    const warnings = Array.isArray(result.warnings) ? result.warnings : [];
    if (warnings.length > 0) {
      return {
        type: 'warning',
        text: `${text} ${warnings.join(' ')}`.trim(),
      };
    }
    return { type: 'success', text };
  };

  // Stripe更新モーダルを開く
  const handleOpenStripeUpdateModal = async (product) => {
    if (!canWrite) return;
    if (!product.stripeProductId) {
      setStripeMessage({ type: 'error', text: 'Stripe製品が未作成です' });
      return;
    }

    resetExistingSubscriberPolicy();
    setActionError('');
    setStripeTargetProduct({
      ...product,
      _priceWillChange: null,
    });
    setShowStripeUpdateModal(true);

    try {
      const targetMonthlyPrice = Number(product.monthlyPrice) || 0;
      const preview = await productsRepo.getStripePriceSyncPreview(product.id, {
        monthlyPrice: targetMonthlyPrice,
      });
      setStripeTargetProduct((prev) => (
        prev?.id === product.id
          ? {
            ...prev,
            _priceWillChange: preview.priceWillChange === true,
            _stripeUnitAmount: preview.stripeUnitAmount,
            _targetMonthlyPrice: preview.targetMonthlyPrice ?? targetMonthlyPrice,
          }
          : prev
      ));
    } catch (err) {
      console.error('Stripe価格プレビュー取得エラー:', err);
      setStripeTargetProduct((prev) => (
        prev?.id === product.id
          ? { ...prev, _priceWillChange: true }
          : prev
      ));
    }
  };

  // Stripe更新モーダルを閉じる
  const handleCloseStripeUpdateModal = () => {
    setShowStripeUpdateModal(false);
    setStripeTargetProduct(null);
    setActionError('');
  };

  // Stripe製品更新を実行
  const handleConfirmStripeUpdate = async () => {
    if (!canWrite || !stripeTargetProduct) return;

    setStripeProcessing(stripeTargetProduct.id);
    setStripeMessage(null);

    try {
      const targetMonthlyPrice = Number(stripeTargetProduct.monthlyPrice) || 0;
      const result = await productsRepo.updateStripeProduct(stripeTargetProduct.id, {
        existingSubscriberPolicy,
        monthlyPrice: targetMonthlyPrice,
      });
      setStripeMessage(buildStripeSyncMessage(result));
      handleCloseStripeUpdateModal();
      await loadProducts({ syncEditingProductId: stripeTargetProduct.id, silent: true });
      if (viewMode === 'edit' && editingProduct?.id === stripeTargetProduct.id) {
        setSavedPriceSnapshot({
          monthlyPrice: targetMonthlyPrice,
          isFree: stripeTargetProduct.isFree === true,
        });
      }
    } catch (err) {
      console.error('Stripe製品更新エラー:', err);
      setActionError(err.message || 'Stripe製品の更新に失敗しました');
    } finally {
      setStripeProcessing(null);
    }
  };

  const buildSavePayload = () => {
    if (!editingProduct) return null;

    const visibilityScope = normalizeVisibilityScope(editingProduct);
    const payload = {
      name: editingProduct.name,
      shortDescription: editingProduct.shortDescription,
      description: editingProduct.description,
      features: editingProduct.features,
      useCases: editingProduct.useCases,
      monthlyPrice: editingProduct.monthlyPrice,
      isComingSoon: editingProduct.isComingSoon,
      isFree: editingProduct.isFree,
      freeNote: editingProduct.freeNote,
      priceNote: editingProduct.priceNote,
      iconType: editingProduct.iconType,
      visibilityScope,
      isActive: isActiveForStorage(visibilityScope),
      launchUrl: editingProduct.launchUrl,
      launchUrlDev: editingProduct.launchUrlDev,
      stripePriceId: editingProduct.stripePriceId,
    };

    if (!editingProduct.isFree) {
      const prodTrial = validateTrialDaysField(editingProduct.trialPeriodDays, '本番期の試用日数');
      if (!prodTrial.ok) {
        setStripeMessage({ type: 'error', text: prodTrial.error });
        return null;
      }
      const betaTrial = validateTrialDaysField(editingProduct.betaTrialPeriodDays, 'β期間の試用日数');
      if (!betaTrial.ok) {
        setStripeMessage({ type: 'error', text: betaTrial.error });
        return null;
      }
      payload.trialPeriodDays = prodTrial.value;
      payload.betaTrialPeriodDays = betaTrial.value;
    } else {
      payload.trialPeriodDays = null;
      payload.betaTrialPeriodDays = null;
    }

    if (!editingProduct.id) {
      const licenseKeys = parseLicenseKeysFromInputs(editingProduct.licenseKeyInputs || []);
      if (!licenseKeys.length) {
        setStripeMessage({ type: 'error', text: 'ライセンスキーを1件以上入力してください' });
        return null;
      }
      payload.licenseKeys = licenseKeys;
    }

    return payload;
  };

  const executeSave = async (payload, { syncStripeAfter = false, subscriberPolicy } = {}) => {
    if (!canWrite) return;
    setIsSaving(true);
    setStripeMessage(null);

    try {
      let productId = editingProduct.id;

      if (productId) {
        await productsRepo.updateProduct(productId, payload);
      } else {
        const created = await productsRepo.createProduct(payload);
        productId = created.id;
      }

      if (syncStripeAfter && productId) {
        const result = await productsRepo.updateStripeProduct(productId, {
          existingSubscriberPolicy: subscriberPolicy || existingSubscriberPolicy,
          monthlyPrice: Number(payload.monthlyPrice) || 0,
        });
        setStripeMessage(buildStripeSyncMessage(result, '保存し、'));
      }

      await loadProducts();
      handleBackToList();
    } catch (err) {
      console.error('保存エラー:', err);
      setStripeMessage({ type: 'error', text: err.message || '保存に失敗しました' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseUnsyncedPriceModal = () => {
    setShowUnsyncedPriceModal(false);
    setUnsyncedPriceModalContext(null);
    setActionError('');
  };

  const handleConfirmSaveWithoutStripeSync = async () => {
    if (!unsyncedPriceModalContext?.payload) return;
    handleCloseUnsyncedPriceModal();
    await executeSave(unsyncedPriceModalContext.payload);
  };

  const handleConfirmSaveWithStripeSync = async () => {
    if (!unsyncedPriceModalContext?.payload) return;
    const payload = unsyncedPriceModalContext.payload;
    const subscriberPolicy = existingSubscriberPolicy;
    handleCloseUnsyncedPriceModal();
    await executeSave(payload, { syncStripeAfter: true, subscriberPolicy });
  };

  const handleSave = async (e) => {
    e.preventDefault();

    const payload = buildSavePayload();
    if (!payload) return;

    if (needsStripeSyncAfterPriceChange(editingProduct, savedPriceSnapshot)) {
      resetExistingSubscriberPolicy();
      setUnsyncedPriceModalContext({
        productName: editingProduct.name,
        previousPrice: savedPriceSnapshot.monthlyPrice,
        newPrice: Number(editingProduct.monthlyPrice) || 0,
        payload,
      });
      setActionError('');
      setShowUnsyncedPriceModal(true);
      return;
    }

    await executeSave(payload);
  };

  const handleToggleActive = async (product) => {
    if (!canWrite) return;
    try {
      const currentScope = normalizeVisibilityScope(product);
      const nextScope = currentScope === VISIBILITY_SCOPES.PRIVATE
        ? VISIBILITY_SCOPES.PUBLIC
        : VISIBILITY_SCOPES.PRIVATE;
      await productsRepo.updateProduct(product.id, {
        visibilityScope: nextScope,
        isActive: isActiveForStorage(nextScope),
      });
      await loadProducts();
    } catch (err) {
      console.error('状態更新エラー:', err);
      setStripeMessage({ type: 'error', text: '状態の更新に失敗しました' });
    }
  };

  const handleMoveUp = async (index) => {
    if (!canWrite || index === 0) return;
    
    const newProducts = [...products];
    const temp = newProducts[index];
    newProducts[index] = newProducts[index - 1];
    newProducts[index - 1] = temp;
    
    try {
      await productsRepo.updateMultipleDisplayOrders(
        newProducts.map((p, i) => ({ id: p.id, displayOrder: i + 1 }))
      );
      await loadProducts();
    } catch (err) {
      console.error('並び替えエラー:', err);
    }
  };

  const handleMoveDown = async (index) => {
    if (!canWrite || index === products.length - 1) return;
    
    const newProducts = [...products];
    const temp = newProducts[index];
    newProducts[index] = newProducts[index + 1];
    newProducts[index + 1] = temp;
    
    try {
      await productsRepo.updateMultipleDisplayOrders(
        newProducts.map((p, i) => ({ id: p.id, displayOrder: i + 1 }))
      );
      await loadProducts();
    } catch (err) {
      console.error('並び替えエラー:', err);
    }
  };

  const addCreateLicenseKey = (key) => {
    const normalized = normalizeLicenseKey(key);
    if (!normalized) return;
    const current = parseLicenseKeysFromInputs(editingProduct.licenseKeyInputs || []);
    if (current.includes(normalized)) return;
    if (current.length >= MAX_LICENSE_KEYS_PER_PRODUCT) return;
    const inputs = licenseKeyInputsFromProduct({ licenseKeys: [...current, normalized] });
    setEditingProduct(prev => ({ ...prev, licenseKeyInputs: inputs }));
  };

  const removeCreateLicenseKey = (key) => {
    const current = parseLicenseKeysFromInputs(editingProduct.licenseKeyInputs || []);
    const next = current.filter((k) => k !== key);
    const inputs = licenseKeyInputsFromProduct({ licenseKeys: next });
    setEditingProduct(prev => ({ ...prev, licenseKeyInputs: inputs }));
  };

  const swapCreateLicenseKey = (oldKey, newKey) => {
    const normalized = normalizeLicenseKey(newKey);
    if (!normalized) return;
    const current = parseLicenseKeysFromInputs(editingProduct.licenseKeyInputs || []);
    const next = current.map((k) => (k === oldKey ? normalized : k));
    const inputs = licenseKeyInputsFromProduct({ licenseKeys: next });
    setEditingProduct(prev => ({ ...prev, licenseKeyInputs: inputs }));
  };

  const moveCreateLicenseKey = (key, direction) => {
    const current = parseLicenseKeysFromInputs(editingProduct.licenseKeyInputs || []);
    const idx = current.indexOf(key);
    if (idx < 0) return;
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= current.length) return;
    const next = [...current];
    next[idx] = current[target];
    next[target] = current[idx];
    const inputs = licenseKeyInputsFromProduct({ licenseKeys: next });
    setEditingProduct(prev => ({ ...prev, licenseKeyInputs: inputs }));
  };

  const handleProductLicenseKeysUpdated = (result) => {
    if (!result?.licenseKeys) return;
    setEditingProduct((prev) => ({
      ...prev,
      licenseKeys: result.licenseKeys,
      licenseName: result.licenseKeys[0] || prev.licenseName
    }));
  };

  const updateEditingField = (field, value) => {
    setEditingProduct(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFeaturesChange = (text) => {
    const features = text.split('\n').filter(f => f.trim() !== '');
    updateEditingField('features', features);
  };

  const handleUseCasesChange = (text) => {
    const useCases = text.split('\n').filter(u => u.trim() !== '');
    updateEditingField('useCases', useCases);
  };

  const formatPrice = (price) => {
    if (price === 0) return '無料';
    return `¥${price.toLocaleString()}`;
  };

  if (isLoading) {
    return <AdminCardGridShimmer cards={6} />;
  }

  if (error) {
    return (
      <div className="admin-error">
        <p>{error}</p>
        <button onClick={loadProducts} className="btn btn-primary">
          再読み込み
        </button>
      </div>
    );
  }

  return (
    <div className="admin-products">
      {stripeMessage && (
        <div className={`stripe-message ${stripeMessage.type}`}>
          <span>{stripeMessage.text}</span>
          <button onClick={() => setStripeMessage(null)} className="close-btn">×</button>
        </div>
      )}

      {viewMode === 'edit' && editingProduct ? (
        <div className="product-edit">
          <button type="button" className="product-edit-back" onClick={handleBackToList}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            プロダクト一覧に戻る
          </button>

          <div className="product-edit-header">
            <h1>{editingProduct.id ? '製品を編集' : '新規製品'}</h1>
            <p>{editingProduct.id ? editingProduct.name : '新しい製品・モジュールを登録します'}</p>
          </div>

          <form onSubmit={handleSave} className="product-edit-form">
            <section className="product-form-section product-form-section--highlight">
              <h2>起動 URL（ヘルスチェック連携）</h2>
              <p className="section-desc">
                ユーザー向け起動先と、ヘルスチェック画面での死活監視に使用する URL です。
              </p>
              <div className="form-group">
                <label>起動URL（本番）</label>
                <input
                  type="url"
                  value={editingProduct.launchUrl || ''}
                  onChange={e => updateEditingField('launchUrl', e.target.value)}
                  placeholder="https://example.com/app"
                />
                <p className="field-hint">
                  ユーザーが「起動」ボタンを押した時にリダイレクトする URL を設定します。
                </p>
              </div>
              <div className="form-group">
                <label>起動URL（開発環境用）</label>
                <input
                  type="url"
                  value={editingProduct.launchUrlDev || ''}
                  onChange={e => updateEditingField('launchUrlDev', e.target.value)}
                  placeholder="https://dev.example.com/app"
                />
                <p className="field-hint">
                  開発環境でハブを開いた場合に使用する URL。未設定の場合は本番 URL を使用します。
                </p>
              </div>
            </section>

            <section className="product-form-section">
              <h2>基本情報</h2>
              <div className="form-group">
                <label>製品名 <span className="required">*</span></label>
                <input
                  type="text"
                  value={editingProduct.name || ''}
                  onChange={e => updateEditingField('name', e.target.value)}
                  required
                  placeholder="例: Synapse マイオ"
                />
                <p className="field-hint">
                  ユーザー向けの表示名です。ライセンスキー（契約照合キー）とは独立して設定できます。
                </p>
              </div>

              <div className="form-group license-keys-group">
                <label>ライセンスキー <span className="required">*</span></label>
                {editingProduct.id ? (
                  <ProductLicenseKeyEditor
                    productId={editingProduct.id}
                    product={editingProduct}
                    onUpdated={handleProductLicenseKeysUpdated}
                  />
                ) : (
                  <div className="license-key-list">
                      {createLicenseKeys.map((key, index, arr) => (
                        <RegisteredLicenseKeySelectRow
                          key={`${index}-${key}`}
                          slotLabel={`キー${index + 1}`}
                          value={key}
                          selectOptions={buildSelectableKeysForProductSlot(
                            registeredLicenseKeys,
                            createLicenseKeys,
                            key
                          )}
                          isPrimary={index === 0}
                          canMoveUp={index > 0}
                          canMoveDown={index < arr.length - 1}
                          onSwap={swapCreateLicenseKey}
                          onDelete={removeCreateLicenseKey}
                          onMoveUp={() => moveCreateLicenseKey(key, 'up')}
                          onMoveDown={() => moveCreateLicenseKey(key, 'down')}
                          disabled={registryKeysLoading}
                        />
                      ))}
                      {Array.from({
                        length: MAX_LICENSE_KEYS_PER_PRODUCT - createLicenseKeys.length
                      }).map((_, i) => (
                        <EmptyLicenseSlotSelectRow
                          key={`create-empty-${i}`}
                          slotLabel="追加"
                          selectOptions={createSelectableKeys}
                          onAdd={addCreateLicenseKey}
                          disabled={registryKeysLoading}
                          emptyHint="ライセンス管理で登録済みの未使用キーがありません"
                        />
                      ))}
                    </div>
                )}
              </div>

              <div className="form-group">
                <label>短い説明 <span className="required">*</span></label>
                <input
                  type="text"
                  value={editingProduct.shortDescription || ''}
                  onChange={e => updateEditingField('shortDescription', e.target.value)}
                  required
                  placeholder="例: 小児口腔機能発達不全の検査支援"
                />
              </div>

              <div className="form-group">
                <label>詳細説明</label>
                <textarea
                  value={editingProduct.description || ''}
                  onChange={e => updateEditingField('description', e.target.value)}
                  rows={3}
                  placeholder="製品の詳細な説明を入力してください"
                />
              </div>

              <div className="form-group">
                <label>アイコンタイプ</label>
                <select
                  className="admin-select"
                  value={editingProduct.iconType || 'default'}
                  onChange={e => updateEditingField('iconType', e.target.value)}
                >
                  {iconOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </section>

            <section className="product-form-section">
              <h2>機能・活用シーン</h2>
              <div className="form-group">
                <label>機能一覧（1行に1つ）</label>
                <textarea
                  value={(editingProduct.features || []).join('\n')}
                  onChange={e => handleFeaturesChange(e.target.value)}
                  rows={4}
                  placeholder="検査データの記録&#10;自動レポート生成&#10;経過観察機能"
                />
              </div>

              <div className="form-group">
                <label>活用シーン（1行に1つ）</label>
                <textarea
                  value={(editingProduct.useCases || []).join('\n')}
                  onChange={e => handleUseCasesChange(e.target.value)}
                  rows={4}
                  placeholder="初診時の検査&#10;定期的な経過確認&#10;患者説明"
                />
              </div>
            </section>

            <section className="product-form-section">
              <h2>料金・Stripe</h2>
              <div className="form-group">
                <label>月額料金（税別・円）</label>
                <input
                  type="number"
                  value={editingProduct.monthlyPrice || 0}
                  onChange={e => updateEditingField('monthlyPrice', parseInt(e.target.value) || 0)}
                  min={0}
                  disabled={editingProduct.isFree}
                />
                <p className="field-hint">
                  入力額は税別です。Stripe Price は tax_behavior=exclusive で作成され、消費税 10% は決済時に Stripe Tax / Tax Rate で加算されます。
                  {editingProduct.taxConfigured === true && editingProduct.priceTaxBehavior === 'exclusive'
                    ? '（税設定済み）'
                    : '（未整備の場合は会計画面の「税別・税設定を整備」を実行）'}
                </p>
              </div>

              {!editingProduct.isFree && (
                <div className="form-group">
                  <label>有料時の料金注記（任意）</label>
                  <textarea
                    value={editingProduct.priceNote || ''}
                    onChange={e => updateEditingField('priceNote', e.target.value)}
                    rows={2}
                    placeholder="例: 現在の価格は期間限定です。今後変更する場合があります。"
                  />
                  <p className="field-hint">
                    料金ページ・ライセンス画面など、月額表示の横に括弧付きで表示されます。
                  </p>
                </div>
              )}

              <div className="form-group">
                <label className="checkbox-label-inline">
                  <input
                    type="checkbox"
                    checked={editingProduct.isFree || false}
                    onChange={e => updateEditingField('isFree', e.target.checked)}
                  />
                  無料で提供
                </label>
                {editingProduct.isFree && (
                  <input
                    type="text"
                    value={editingProduct.freeNote || ''}
                    onChange={e => updateEditingField('freeNote', e.target.value)}
                    placeholder="注記（例: 期間限定）"
                    style={{ marginTop: '8px' }}
                  />
                )}
              </div>

              {!editingProduct.isFree && (
                <div className="form-group product-trial-settings">
                  <label>初期試用日数（本番期）</label>
                  <input
                    type="number"
                    min={0}
                    max={365}
                    value={editingProduct.trialPeriodDays ?? ''}
                    onChange={(e) => updateEditingField('trialPeriodDays', e.target.value)}
                    placeholder="未設定（ハブ設定に従う）"
                    disabled={editingProduct.isFree}
                  />
                  <label style={{ marginTop: '12px' }}>初期試用日数（β期間）</label>
                  <input
                    type="number"
                    min={0}
                    max={365}
                    value={editingProduct.betaTrialPeriodDays ?? ''}
                    onChange={(e) => updateEditingField('betaTrialPeriodDays', e.target.value)}
                    placeholder="未設定（ハブ β 設定に従う）"
                    disabled={editingProduct.isFree}
                  />
                  <p className="field-hint">
                    空欄の場合はハブ設定の試用日数を使用します。0 を指定すると試用なし（即課金）です。
                    β期間中は<strong> β 用の試用日数</strong>（未設定時はハブの β 試用日数）が優先され、本番用の値は参照されません。
                    無料製品では試用は付与されません。
                  </p>
                </div>
              )}

              {!editingProduct.isFree && (
                <div className="form-group stripe-settings">
                  <label>
                    Stripe 価格ID
                    <span className="label-hint">（有料製品の場合は必須）</span>
                  </label>
                  <input
                    type="text"
                    value={editingProduct.stripePriceId || ''}
                    onChange={e => updateEditingField('stripePriceId', e.target.value)}
                    placeholder="price_xxxxxxxxxxxxxxxx"
                    className={!editingProduct.stripePriceId && editingProduct.monthlyPrice > 0 ? 'warning' : ''}
                  />
                  {!editingProduct.stripePriceId && editingProduct.monthlyPrice > 0 && (
                    <p className="field-warning field-warning-with-icon">
                      <IconAlertTriangle size={16} />
                      価格IDが未設定です。ユーザーはこの製品を契約できません。
                    </p>
                  )}
                  <p className="field-hint">
                    Stripeダッシュボードで作成した価格のIDを入力してください。
                    <a
                      href="https://dashboard.stripe.com/products"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="external-link"
                    >
                      Stripe Products →
                    </a>
                  </p>
                  {editingProduct.id ? (
                    <div className="stripe-settings-actions">
                      {!editingProduct.stripePriceId ? (
                        <button
                          type="button"
                          className="btn btn-secondary btn-stripe-create"
                          onClick={() => handleOpenStripeCreateModal(editingProduct)}
                          disabled={stripeProcessing === editingProduct.id}
                        >
                          {stripeProcessing === editingProduct.id ? '処理中...' : 'Stripeに作成'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-secondary btn-stripe-sync"
                          onClick={() => handleOpenStripeUpdateModal(editingProduct)}
                          disabled={stripeProcessing === editingProduct.id || !editingProduct.stripeProductId}
                        >
                          {stripeProcessing === editingProduct.id ? '処理中...' : 'Stripe同期'}
                        </button>
                      )}
                    </div>
                  ) : (
                    <p className="field-hint stripe-settings-save-hint">
                      Stripe連携（作成・同期）は製品を保存した後に利用できます。
                    </p>
                  )}
                </div>
              )}
            </section>

            <section className="product-form-section">
              <h2>公開設定</h2>
              <div className="form-group">
                <label className="checkbox-label-inline">
                  <input
                    type="checkbox"
                    checked={editingProduct.isComingSoon || false}
                    onChange={e => updateEditingField('isComingSoon', e.target.checked)}
                  />
                  準備中（Coming Soon）
                </label>
              </div>

              <fieldset className="product-visibility-fieldset">
                <legend>公開範囲</legend>
                {VISIBILITY_SCOPE_OPTIONS.map((option) => (
                  <label key={option.value} className="product-visibility-option">
                    <input
                      type="radio"
                      name="product-visibility-scope"
                      value={option.value}
                      checked={normalizeVisibilityScope(editingProduct) === option.value}
                      onChange={() => updateEditingField('visibilityScope', option.value)}
                    />
                    <span className="product-visibility-option-text">
                      <strong>{option.label}</strong>
                      <span>{option.description}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
            </section>

            <div className="product-edit-footer">
              <button
                type="button"
                onClick={handleBackToList}
                className="btn btn-secondary"
                disabled={isSaving}
              >
                キャンセル
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSaving}
              >
                {isSaving ? '保存中...' : '保存'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <>
      <div className="section-header">
        <div>
          <h1>プロダクト管理</h1>
          <p>{canWrite ? '製品・モジュールの内容、説明、価格を管理します（カードをクリックで編集）' : '製品・モジュールの内容、説明、価格を確認します'}</p>
        </div>
        {canWrite && (
        <button onClick={handleCreateNew} className="btn btn-primary">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          新規追加
        </button>
        )}
      </div>

      <div className="products-card-list">
        {products.length === 0 ? (
          <div className="empty-state product-empty-state">
            <p>製品がまだ登録されていません</p>
            {canWrite && (
            <button onClick={handleCreateNew} className="btn btn-primary">
              最初の製品を追加
            </button>
            )}
          </div>
        ) : (
          products.map((product, index) => (
            <article
              key={product.id}
              className={`product-card${canWrite ? ' clickable' : ''}${normalizeVisibilityScope(product) === VISIBILITY_SCOPES.PRIVATE ? ' inactive' : ''}`}
              onClick={canWrite ? (e) => handleCardClick(product, e) : undefined}
              onKeyDown={canWrite ? (e) => handleCardKeyDown(product, e) : undefined}
              role={canWrite ? 'button' : undefined}
              tabIndex={canWrite ? 0 : undefined}
              aria-label={canWrite ? `${product.name} を編集` : product.name}
            >
              <div className="product-card-top">
                <div className="product-card-order">
                  {canWrite ? (
                    <>
                  <button
                    type="button"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    className="order-btn"
                    title="上に移動"
                    aria-label="上に移動"
                  >
                    <IconChevronUp size={14} />
                  </button>
                  <span className="product-card-order-num">{index + 1}</span>
                  <button
                    type="button"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === products.length - 1}
                    className="order-btn"
                    title="下に移動"
                    aria-label="下に移動"
                  >
                    <IconChevronDown size={14} />
                  </button>
                    </>
                  ) : (
                    <span className="product-card-order-num">{index + 1}</span>
                  )}
                </div>

                <div className="product-card-main">
                  <div className="product-card-title-row">
                    <h3 className="product-card-name">{product.name}</h3>
                    <div className="product-card-badges">
                      {product.isComingSoon ? (
                        <span className="badge badge-warning">準備中</span>
                      ) : (
                        <span className="badge badge-success">提供中</span>
                      )}
                      {normalizeVisibilityScope(product) === VISIBILITY_SCOPES.STAGING && (
                        <span className="badge badge-info">ステージングのみ</span>
                      )}
                      {normalizeVisibilityScope(product) === VISIBILITY_SCOPES.UNLISTED && (
                        <span className="badge badge-info">限定公開</span>
                      )}
                      {normalizeVisibilityScope(product) === VISIBILITY_SCOPES.PRIVATE && (
                        <span className="badge badge-neutral">非公開</span>
                      )}
                    </div>
                  </div>
                  {product.shortDescription && (
                    <p className="product-card-desc">{product.shortDescription}</p>
                  )}
                  {(product.licenseKeys?.length || product.licenseName) && (
                    <p className="product-card-license-keys">
                      キー: {(product.licenseKeys || [product.licenseName]).join(', ')}
                    </p>
                  )}
                </div>

                {(canWrite || canDelete) && (
                <div className="product-actions-menu">
                  <button
                    type="button"
                    className="menu-trigger"
                    onClick={() => setOpenMenuId(openMenuId === product.id ? null : product.id)}
                    aria-label="操作メニュー"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="12" cy="5" r="2"/>
                      <circle cx="12" cy="12" r="2"/>
                      <circle cx="12" cy="19" r="2"/>
                    </svg>
                  </button>
                  {openMenuId === product.id && (
                    <div className="actions-dropdown">
                      {canWrite && (
                      <button
                        type="button"
                        onClick={() => openEditView(product)}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                        編集
                      </button>
                      )}
                      {canWrite && !product.isFree && product.monthlyPrice > 0 && (
                        <>
                          {!product.stripePriceId ? (
                            <button
                              type="button"
                              onClick={() => {
                                handleOpenStripeCreateModal(product);
                                setOpenMenuId(null);
                              }}
                              disabled={stripeProcessing === product.id}
                              className="stripe"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                                <line x1="1" y1="10" x2="23" y2="10"/>
                              </svg>
                              {stripeProcessing === product.id ? '処理中...' : 'Stripeに作成'}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                handleOpenStripeUpdateModal(product);
                                setOpenMenuId(null);
                              }}
                              disabled={stripeProcessing === product.id}
                              className="stripe-update"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 2v6h-6"/>
                                <path d="M3 12a9 9 0 0 1 15-6.7L21 8"/>
                                <path d="M3 22v-6h6"/>
                                <path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
                              </svg>
                              {stripeProcessing === product.id ? '処理中...' : 'Stripe同期'}
                            </button>
                          )}
                        </>
                      )}
                      {canDelete && (
                      <button
                        type="button"
                        onClick={() => {
                          handleOpenDeleteModal(product);
                          setOpenMenuId(null);
                        }}
                        className="danger"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                        削除
                      </button>
                      )}
                    </div>
                  )}
                </div>
                )}
              </div>

              <div className="product-card-footer">
                <div className="product-card-meta-item">
                  <span className="meta-label">{product.isFree ? '月額' : '月額（税別）'}</span>
                  <span className="product-card-price">
                    {product.isFree ? (
                      <>
                        <span className="price-free">無料</span>
                        {product.freeNote && <small>（{product.freeNote}）</small>}
                      </>
                    ) : (
                      <>
                        {formatPrice(product.monthlyPrice)}/月
                        {product.taxConfigured === true && product.priceTaxBehavior === 'exclusive' && (
                          <span className="product-tax-exclusive-badge">税別</span>
                        )}
                        {product.priceNote && <small>（{product.priceNote}）</small>}
                      </>
                    )}
                  </span>
                </div>

                <div className="product-card-meta-item">
                  <span className="meta-label">Stripe</span>
                  {product.isFree ? (
                    <span className="badge badge-neutral">不要</span>
                  ) : product.stripePriceId ? (
                    <span className="badge badge-success" title={product.stripePriceId}>連携済</span>
                  ) : (
                    <span className="badge badge-error">未設定</span>
                  )}
                </div>

                <div className="product-card-meta-item product-card-meta-item--wide">
                  <span className="meta-label">起動URL</span>
                  <span className="product-launch-urls">
                    <ProductLaunchUrlLine label="本番" href={product.launchUrl} />
                    <ProductLaunchUrlLine label="開発" href={product.launchUrlDev} />
                  </span>
                </div>

                <div className="product-card-meta-item product-card-meta-item--action">
                  <span className="meta-label">公開</span>
                  {canWrite ? (
                  <button
                    type="button"
                    onClick={() => handleToggleActive(product)}
                    className={`toggle-btn ${normalizeVisibilityScope(product) !== VISIBILITY_SCOPES.PRIVATE ? 'active' : ''}`}
                    title={
                      normalizeVisibilityScope(product) === VISIBILITY_SCOPES.STAGING
                        ? 'ステージングのみ（編集画面で変更）'
                        : normalizeVisibilityScope(product) === VISIBILITY_SCOPES.UNLISTED
                          ? '限定公開（編集画面で変更）'
                          : normalizeVisibilityScope(product) !== VISIBILITY_SCOPES.PRIVATE
                            ? '非公開にする'
                            : '公開にする'
                    }
                    disabled={
                      normalizeVisibilityScope(product) === VISIBILITY_SCOPES.STAGING
                      || normalizeVisibilityScope(product) === VISIBILITY_SCOPES.UNLISTED
                    }
                  >
                    {normalizeVisibilityScope(product) === VISIBILITY_SCOPES.STAGING
                      ? 'STG'
                      : normalizeVisibilityScope(product) === VISIBILITY_SCOPES.UNLISTED
                        ? 'LTD'
                        : normalizeVisibilityScope(product) !== VISIBILITY_SCOPES.PRIVATE
                          ? '公開'
                          : '非公開'}
                  </button>
                  ) : (
                    <span className={`toggle-btn toggle-btn-readonly ${normalizeVisibilityScope(product) !== VISIBILITY_SCOPES.PRIVATE ? 'active' : ''}`}>
                      {normalizeVisibilityScope(product) === VISIBILITY_SCOPES.STAGING
                        ? 'STG'
                        : normalizeVisibilityScope(product) === VISIBILITY_SCOPES.UNLISTED
                          ? 'LTD'
                          : normalizeVisibilityScope(product) !== VISIBILITY_SCOPES.PRIVATE
                            ? '公開'
                            : '非公開'}
                    </span>
                  )}
                </div>
              </div>
            </article>
          ))
        )}
      </div>

        </>
      )}

      {/* Stripe製品作成確認モーダル */}
      {showStripeCreateModal && stripeTargetProduct && (
        <div className="modal-overlay" onClick={handleCloseStripeCreateModal}>
          <div className="modal-content" style={{ maxWidth: '480px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Stripe製品の作成</h2>
              <button onClick={handleCloseStripeCreateModal} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <div style={{ 
                padding: '12px 16px',
                background: '#f3f4f6',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <strong>{stripeTargetProduct.name}</strong>
                <div style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '4px' }}>
                  月額（税別）: ¥{stripeTargetProduct.monthlyPrice?.toLocaleString()}
                  <span className="product-tax-exclusive-badge">税別</span>
                </div>
              </div>
              <p style={{ fontSize: '0.875rem', color: '#374151', lineHeight: 1.6 }}>
                この製品をStripeに登録します（金額は税別。消費税は決済時に加算）。登録後、ユーザーはこの製品を購入できるようになります。
              </p>
              {actionError && (
                <div style={{ marginTop: '12px', color: '#dc2626', fontSize: '0.875rem' }}>
                  {actionError}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button onClick={handleCloseStripeCreateModal} className="btn btn-secondary">
                キャンセル
              </button>
              <button 
                onClick={handleConfirmStripeCreate} 
                className="btn btn-primary"
                disabled={stripeProcessing === stripeTargetProduct.id}
              >
                {stripeProcessing === stripeTargetProduct.id ? '処理中...' : 'Stripeに作成'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stripe製品更新確認モーダル */}
      {showStripeUpdateModal && stripeTargetProduct && (
        <div className="modal-overlay" onClick={handleCloseStripeUpdateModal}>
          <div className="modal-content" style={{ maxWidth: '480px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Stripe製品の更新</h2>
              <button onClick={handleCloseStripeUpdateModal} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <div style={{ 
                padding: '12px 16px',
                background: '#f3f4f6',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <strong>{stripeTargetProduct.name}</strong>
                <div style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '4px' }}>
                  月額（税別）: ¥{stripeTargetProduct.monthlyPrice?.toLocaleString()}
                  <span className="product-tax-exclusive-badge">税別</span>
                </div>
              </div>
              {stripeTargetProduct._priceWillChange === null ? (
                <p style={{ fontSize: '0.875rem', color: '#6b7280', lineHeight: 1.6, margin: 0 }}>
                  Stripe の現在価格を確認しています…
                </p>
              ) : stripeTargetProduct._priceWillChange ? (
                <>
                  <div style={{ 
                    padding: '12px 16px',
                    background: '#fffbeb',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    border: '1px solid rgba(245, 158, 11, 0.3)'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: '8px',
                      color: '#92400e',
                      fontSize: '0.875rem',
                      lineHeight: 1.6
                    }}>
                      <IconInfo size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                      <span>
                        料金が変更されています（Stripe: ¥{(stripeTargetProduct._stripeUnitAmount ?? 0).toLocaleString()}
                        {' → '}
                        ¥{(stripeTargetProduct._targetMonthlyPrice ?? stripeTargetProduct.monthlyPrice ?? 0).toLocaleString()}）。
                        新しい価格IDが作成されます。下記で既存契約者の扱いを選んでください。
                      </span>
                    </div>
                  </div>
                  <ExistingSubscriberPolicyField
                    value={existingSubscriberPolicy}
                    onChange={setExistingSubscriberPolicy}
                    namePrefix="stripe-update-subscriber-policy"
                  />
                </>
              ) : (
                <p style={{ fontSize: '0.875rem', color: '#374151', lineHeight: 1.6, margin: 0 }}>
                  料金に変更はありません。製品名などの Stripe 情報のみ更新します。
                </p>
              )}
              {actionError && (
                <div style={{ marginTop: '12px', color: '#dc2626', fontSize: '0.875rem' }}>
                  {actionError}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button onClick={handleCloseStripeUpdateModal} className="btn btn-secondary">
                キャンセル
              </button>
              <button 
                onClick={handleConfirmStripeUpdate} 
                className="btn btn-primary"
                disabled={
                  stripeProcessing === stripeTargetProduct.id
                  || stripeTargetProduct._priceWillChange === null
                }
              >
                {stripeProcessing === stripeTargetProduct.id ? '処理中...' : '更新する'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 料金変更後の Stripe 未同期確認モーダル */}
      {showUnsyncedPriceModal && unsyncedPriceModalContext && (
        <div className="modal-overlay" onClick={handleCloseUnsyncedPriceModal}>
          <div className="modal-content" style={{ maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Stripe同期が必要です</h2>
              <button onClick={handleCloseUnsyncedPriceModal} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <div style={{
                padding: '12px 16px',
                background: '#f3f4f6',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <strong>{unsyncedPriceModalContext.productName}</strong>
                <div style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '4px' }}>
                  月額（税別）: ¥{unsyncedPriceModalContext.previousPrice.toLocaleString()}
                  {' → '}
                  ¥{unsyncedPriceModalContext.newPrice.toLocaleString()}
                </div>
              </div>
              <div style={{
                padding: '12px 16px',
                background: '#fffbeb',
                borderRadius: '8px',
                marginBottom: '16px',
                border: '1px solid rgba(245, 158, 11, 0.3)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                  color: '#92400e',
                  fontSize: '0.875rem',
                  lineHeight: 1.6
                }}>
                  <IconAlertTriangle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>
                    料金を変更しましたが、Stripe同期はまだ実行されていません。
                    保存のみ行うと、ポータル表示は新料金でも、新規契約・請求は旧価格のままになります。
                  </span>
                </div>
              </div>
              <p style={{ fontSize: '0.875rem', color: '#374151', lineHeight: 1.6, margin: '0 0 16px' }}>
                「保存してStripe同期」を選ぶと、保存後に自動で Stripe 価格を更新します。
              </p>
              <ExistingSubscriberPolicyField
                value={existingSubscriberPolicy}
                onChange={setExistingSubscriberPolicy}
                namePrefix="unsynced-price-subscriber-policy"
              />
              {actionError && (
                <div style={{ marginTop: '12px', color: '#dc2626', fontSize: '0.875rem' }}>
                  {actionError}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button onClick={handleCloseUnsyncedPriceModal} className="btn btn-secondary" disabled={isSaving}>
                キャンセル
              </button>
              <button
                onClick={handleConfirmSaveWithoutStripeSync}
                className="btn btn-secondary"
                disabled={isSaving}
              >
                {isSaving ? '処理中...' : '保存のみ'}
              </button>
              <button
                onClick={handleConfirmSaveWithStripeSync}
                className="btn btn-primary"
                disabled={isSaving}
              >
                {isSaving ? '処理中...' : '保存してStripe同期'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProductManagement;

